import * as React from 'react';
import { Control } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFieldInputMergeProps } from '@/hooks/use-merge-props';

/**
 * Props for the FormFieldInputCds component.
 *
 * @typedef {Object} FormFieldInputCdsProps
 * @property {Omit<React.ComponentProps<'input'>, 'form'>} - All standard input props except 'form'.
 * @property {Control} control - The form control object returned by the useForm hook.
 * @property {string | React.ReactNode} [label] - Optional label for the input field.
 * @property {string | React.ReactNode} [description] - Optional description for the input field.
 * @property {string | React.ReactNode} [errorMessage] - Optional error message to display for the input field.
 */
export type FormFieldInputCdsProps = Omit<React.ComponentProps<'input'>, 'form'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any, any>;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  errorMessage?: string | React.ReactNode;
};

/**
 * A React forwardRef component that renders a form field input with various props.
 *
 * @component
 * @param {string} className - Optional additional class name for the component.
 * @param {string} placeholder - Optional placeholder text for the input field.
 * @param {string | React.ReactNode} label - Optional label text for the form field.
 * @param {Control} control - The form control object returned by the useForm hook.
 * @param {string} name - The name of the form field. This is required.
 * @param {string | React.ReactNode} description - Optional description text for the form field.
 * @param {string | React.ReactNode} errorMessage - Optional error message to display.
 * @param {object} props - Additional props to be passed to the component.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to be forwarded to the input element.
 *
 * @throws Will throw an error if the `form` prop is not provided.
 * @throws Will throw an error if the `name` prop is not provided.
 *
 * @returns {JSX.Element} The rendered form field input component.
 */
const FormFieldInputCds = React.forwardRef<HTMLInputElement, FormFieldInputCdsProps>(
  ({ className, placeholder, label, control, name, description, errorMessage, ...props }, ref) => {
    const mergeProps = useFieldInputMergeProps(props);

    if (!name) {
      throw new Error(`FormFieldInputCds 'name' is required (label: ${label})`);
    }

    return (
      <FormField
        {...props}
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className={className} ref={ref}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Input {...mergeProps(field)} placeholder={placeholder || ''} />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage>{errorMessage || ''}</FormMessage>
          </FormItem>
        )}
      />
    );
  }
);
FormFieldInputCds.displayName = 'FormFieldInputCds';

export { FormFieldInputCds };
